import React from 'react';
import { FileText, Users, Target, BarChart3, Settings, CreditCard, UserCog, Zap, Calendar, Wrench, UserCheck, FileCheck, Bell, Activity, GitBranch, Mail } from 'lucide-react';

export default function PageUsageGuide() {
  const pageGuides = [
    {
      page: 'Accounts',
      icon: FileText,
      purpose: 'Manages company-level relationships and organizational data. Tracks companies as business entities with revenue, industry, region, and contact counts. Each account represents a business organization that may have multiple contacts and deals.',
      dataModel: 'Company records with: company name, industry, revenue, region, contact count, deal value, last activity, owner assignment',
      primaryUsers: ['Sales Representatives', 'Account Managers', 'Business Administrators'],
      keyFeatures: ['Company profiles', 'Revenue tracking', 'Industry classification', 'Regional filtering', 'Account ownership'],
      differentiator: 'Accounts = Companies/Organizations. Contacts = Individual people within those companies.'
    },
    {
      page: 'Contacts',
      icon: Users,
      purpose: 'Manages individual people and their personal information. Stores contact details for individuals who work at accounts (companies). Contacts are the actual people you communicate with, while accounts are the companies they work for.',
      dataModel: 'Individual person records with: name, email, phone, company association, title, department, status, type (lead/customer/partner)',
      primaryUsers: ['Sales Representatives', 'Customer Success', 'Marketing Teams'],
      keyFeatures: ['Personal contact info', 'Company association', 'Communication history', 'Contact type classification', 'Status tracking'],
      differentiator: 'Contacts = Individual people. Customers = Paying clients with subscription/billing data. Contacts can become customers.'
    },
    {
      page: 'Deals',
      icon: Target,
      purpose: 'Tracks specific sales opportunities and revenue potential. Manages individual sales transactions from initial opportunity through close. Each deal represents a potential sale with defined value, timeline, and probability.',
      dataModel: 'Sales opportunity records with: deal name, account, value, owner, stage, close date, probability, creation date',
      primaryUsers: ['Sales Representatives', 'Sales Managers', 'Revenue Operations'],
      keyFeatures: ['Deal value tracking', 'Stage progression', 'Probability scoring', 'Close date forecasting', 'Owner assignment'],
      differentiator: 'Deals = Specific sales opportunities. Pipeline = Visual workflow of all deals across stages. Activities = Tasks/actions related to deals.'
    },
    {
      page: 'Reports',
      icon: BarChart3,
      purpose: 'Provides analytics and business intelligence dashboards. Aggregates data from across the CRM to show performance metrics, trends, and insights. Generates visual reports on revenue, conversion rates, sales velocity, and win rates.',
      dataModel: 'Aggregated analytics data: revenue trends, conversion metrics, deal statistics, performance KPIs, time-series data',
      primaryUsers: ['Sales Managers', 'Business Administrators', 'Executives', 'Revenue Operations'],
      keyFeatures: ['Revenue charts', 'Conversion funnels', 'Win rate analysis', 'Sales velocity metrics', 'Custom date ranges', 'Export capabilities'],
      differentiator: 'Reports = Historical analytics and insights. Dashboard = Real-time operational overview.'
    },
    {
      page: 'Settings',
      icon: Settings,
      purpose: 'Central configuration hub for user preferences and system settings. Manages personal profile, company information, team members, custom fields, and pipeline configuration. Provides tabs for different setting categories.',
      dataModel: 'Configuration data: user profiles, company details, team member info, custom field definitions, pipeline stages',
      primaryUsers: ['All Users (for personal settings)', 'Business Administrators (for company/team settings)'],
      keyFeatures: ['Profile management', 'Company branding', 'Team administration', 'Custom field creation', 'Pipeline customization'],
      differentiator: 'Settings = User/company configuration. Integrations = Third-party app connections.'
    },
    {
      page: 'Billing',
      icon: CreditCard,
      purpose: 'Manages subscription plans, payment methods, and billing history. Tracks usage metrics against plan limits. Allows Super Admins to create/edit subscription tiers. Regular users view their current plan and invoices.',
      dataModel: 'Subscription data: tier details (name, price, features, limits), payment methods, invoice history, usage metrics',
      primaryUsers: ['Super Administrators (full management)', 'Business Administrators (view/upgrade)', 'Billing Contacts'],
      keyFeatures: ['Plan management', 'Usage tracking', 'Invoice history', 'Payment method updates', 'Tier creation (Super Admin only)'],
      differentiator: 'Billing = Subscription/payment management. Invoices = Customer-facing billing documents you send to clients.'
    },
    {
      page: 'Users',
      icon: UserCog,
      purpose: 'Manages user accounts and role assignments within the tenant. Creates new users, assigns roles, and tracks user profiles. Integrates with role hierarchy system to control access levels.',
      dataModel: 'User profile records: email, name, role assignment, department, phone, tenant association, creation date',
      primaryUsers: ['Super Administrators', 'Business Administrators', 'HR Managers'],
      keyFeatures: ['User creation', 'Role assignment', 'Profile management', 'User filtering', 'Role hierarchy display'],
      differentiator: 'Users = Internal team members with system access. Contacts = External people you do business with.'
    },
    {
      page: 'Integrations',
      icon: Zap,
      purpose: 'Connects CRM with external services and applications. Manages OAuth connections to email providers (Gmail, Outlook), communication tools (Slack, Zoom), payment processors (Stripe), and productivity apps (Zapier, HubSpot).',
      dataModel: 'Integration connection records: provider name, category, connection status, last sync time, notification preferences',
      primaryUsers: ['Business Administrators', 'IT Administrators', 'Integration Specialists'],
      keyFeatures: ['OAuth connection setup', 'Sync status monitoring', 'Notification preferences', 'Category filtering', 'Connection wizards'],
      differentiator: 'Integrations = External app connections. Settings = Internal CRM configuration.'
    },
    {
      page: 'Calendar',
      icon: Calendar,
      purpose: 'Schedules and manages appointments and meetings. Provides month/week/day views of scheduled appointments. Links appointments to services, customers, and team members. Tracks appointment status and availability.',
      dataModel: 'Appointment records: start/end time, service type, customer, assigned staff, status, notes, tenant association',
      primaryUsers: ['Service Providers', 'Sales Representatives', 'Customer Success', 'Schedulers'],
      keyFeatures: ['Multi-view calendar (month/week/day)', 'Appointment creation', 'Service association', 'Status tracking', 'Filtering by service/staff'],
      differentiator: 'Calendar = Scheduled appointments. Activities = All tasks/actions including calls, emails, meetings, notes.'
    },
    {
      page: 'Services',
      icon: Wrench,
      purpose: 'Configures service offerings and categories for appointment booking. Defines services with pricing, duration, and descriptions. Organizes services into categories for better management.',
      dataModel: 'Service configuration: service name, description, duration, price, category, active status, tenant association',
      primaryUsers: ['Business Administrators', 'Service Managers', 'Operations Teams'],
      keyFeatures: ['Service creation/editing', 'Category management', 'Pricing configuration', 'Duration settings', 'Active/inactive toggle'],
      differentiator: 'Services = Offerings you provide. Products = Physical/digital goods you sell (not yet implemented).'
    },
    {
      page: 'Customers',
      icon: UserCheck,
      purpose: 'Manages paying clients with subscription and billing information. Tracks customer lifecycle from trial to active/cancelled. Stores company details, revenue data, and subscription status. Different from Contacts - these are organizations that pay you.',
      dataModel: 'Customer records: company name, contact person, email, industry, annual revenue, subscription status, customer since date',
      primaryUsers: ['Account Managers', 'Customer Success', 'Billing Teams', 'Sales Representatives'],
      keyFeatures: ['Customer profiles', 'Subscription tracking', 'Revenue monitoring', 'Industry filtering', 'Status management'],
      differentiator: 'Customers = Paying organizations with subscriptions. Contacts = Individual people (may or may not be customers). Accounts = All companies (prospects + customers).'
    },
    {
      page: 'Invoices',
      icon: FileCheck,
      purpose: 'Creates and manages customer-facing billing documents. Generates invoices with line items, calculates totals, and tracks payment status. Integrates with Stripe for payment processing. Sends invoices to customers.',
      dataModel: 'Invoice records: invoice number, customer, line items (description/quantity/price), subtotal, tax, total, payment status, due date',
      primaryUsers: ['Billing Administrators', 'Account Managers', 'Finance Teams'],
      keyFeatures: ['Invoice creation', 'Line item management', 'Payment tracking', 'Stripe integration', 'Invoice sending', 'Status updates'],
      differentiator: 'Invoices = Bills you send to customers. Billing = Your own subscription management. Deals = Sales opportunities (pre-sale).'
    },
    {
      page: 'Notifications',
      icon: Bell,
      purpose: 'Manages automated email notification system. Tracks notification logs (sent/failed/pending), configures notification preferences, and manages email templates. Uses Resend integration for email delivery.',
      dataModel: 'Notification data: logs (status, type, recipient, subject, date), preferences (enabled/disabled by type), templates (content, variables)',
      primaryUsers: ['System Administrators', 'Business Administrators', 'Operations Teams'],
      keyFeatures: ['Notification logs', 'Status filtering', 'Preference management', 'Template configuration', 'Email tracking'],
      differentiator: 'Notifications = Automated system emails. Emails page = Manual email communication with contacts.'
    },
    {
      page: 'Activities',
      icon: Activity,
      purpose: 'Tracks all sales and customer engagement actions. Records tasks, calls, meetings, emails, and notes. Each activity has a type, due date, priority, owner, and can be linked to contacts/accounts. Provides timeline view of all interactions.',
      dataModel: 'Activity records: type (task/call/meeting/email/note), title, description, due date, priority, owner, contact, account, completion status',
      primaryUsers: ['Sales Representatives', 'Account Managers', 'Customer Success'],
      keyFeatures: ['Activity creation', 'Type classification', 'Priority levels', 'Due date tracking', 'Completion status', 'Contact/account linking'],
      differentiator: 'Activities = All tasks and interactions (broad). Deals = Sales opportunities (specific). Pipeline = Visual deal workflow. Activities support deals but exist independently.'
    },
    {
      page: 'Pipeline',
      icon: GitBranch,
      purpose: 'Visualizes deal progression through sales stages. Provides drag-and-drop Kanban board showing deals in columns (New, Qualified, Proposal, Won, Lost). Focuses on visual deal management and stage transitions.',
      dataModel: 'Pipeline view of deals: stage columns, deal cards with value/owner/close date, drag-drop functionality, stage-based filtering',
      primaryUsers: ['Sales Representatives', 'Sales Managers', 'Revenue Operations'],
      keyFeatures: ['Kanban board view', 'Drag-drop stage changes', 'Deal cards', 'Stage statistics', 'Visual deal flow'],
      differentiator: 'Pipeline = Visual Kanban workflow of deals. Deals = Table/list view of opportunities. Activities = Tasks supporting deals. Pipeline shows WHERE deals are, Activities show WHAT you\'re doing.'
    },
    {
      page: 'Emails',
      icon: Mail,
      purpose: 'Integrates with Gmail/Outlook for email communication management. Syncs email threads, allows composing emails, and tracks email interactions with contacts. Requires OAuth connection to email provider.',
      dataModel: 'Email thread data: sender, recipient, subject, preview, body, date, read status, attachments, labels, account association',
      primaryUsers: ['Sales Representatives', 'Account Managers', 'Customer Success'],
      keyFeatures: ['Email provider connection', 'Thread synchronization', 'Email composer', 'Search and filtering', 'Account linking'],
      differentiator: 'Emails = Manual communication with contacts via Gmail/Outlook. Notifications = Automated system emails. Activities = Can include email activities but broader scope.'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">About This Guide</h3>
        <p className="text-blue-800 text-sm">
          This guide explains how each page in LocalProCRM actually works based on the implemented code, data models, and functionality.
          It clarifies the differences between similar pages and identifies who should use each feature.
        </p>
      </div>

      <div className="space-y-4">
        {pageGuides?.map((guide, index) => {
          const IconComponent = guide?.icon;
          return (
            <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{guide?.page}</h3>
                    <p className="text-sm text-gray-600">Primary Users: {guide?.primaryUsers?.join(', ')}</p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                    Purpose & Functionality
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">{guide?.purpose}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                    Data Model
                  </h4>
                  <p className="text-gray-600 text-sm font-mono bg-gray-50 p-3 rounded border border-gray-200">
                    {guide?.dataModel}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                    Key Features
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {guide?.keyFeatures?.map((feature, idx) => (
                      <span key={idx} className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {guide?.differentiator && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <span className="text-lg">💡</span>
                      Key Difference
                    </h4>
                    <p className="text-amber-800 text-sm font-medium">{guide?.differentiator}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-bold text-indigo-900 mb-4">Quick Reference: Common Confusions</h3>
        <div className="space-y-3 text-sm">
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <p className="font-semibold text-gray-900 mb-1">Customers vs Contacts vs Accounts</p>
            <p className="text-gray-700"><strong>Accounts</strong> = All companies (prospects + customers). <strong>Contacts</strong> = Individual people at those companies. <strong>Customers</strong> = Paying organizations with active subscriptions.</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <p className="font-semibold text-gray-900 mb-1">Activities vs Deals vs Pipeline</p>
            <p className="text-gray-700"><strong>Activities</strong> = All tasks/actions (calls, emails, meetings, notes). <strong>Deals</strong> = Specific sales opportunities. <strong>Pipeline</strong> = Visual Kanban board showing deal stages.</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <p className="font-semibold text-gray-900 mb-1">Invoices vs Billing</p>
            <p className="text-gray-700"><strong>Invoices</strong> = Bills you send TO customers. <strong>Billing</strong> = Your own subscription management (what you pay FOR the CRM).</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <p className="font-semibold text-gray-900 mb-1">Emails vs Notifications</p>
            <p className="text-gray-700"><strong>Emails</strong> = Manual communication via Gmail/Outlook integration. <strong>Notifications</strong> = Automated system emails triggered by events.</p>
          </div>
        </div>
      </div>
    </div>
  );
}